<i18n src="./resources/locales.json"></i18n>

<template>
  <div
    id="agentInformationCenter"
    v-intersect="(entries, observer) => onIntersect(entries, observer)"
    :class="isSupervisor ? 'is-supervisor' : ''"
  >
    <div class="grid">
      <div
        v-for="(card, index) in wallboardCards"
        :id="card._id"
        :key="index"
        class="item"
        :style="customCardSize(card)"
      >
        <EcsWallboardCard
          :key="card._id || index"
          :freeze-wallboard="freezeWallboard"
          :disabled="isLoading"
          :card="card"
          class="item-content"
          @openModal="openCardSettingsModal"
          @syncWallboardSlides="toggleWallboardUpdate"
          @resize="onResize"
        />
      </div>
    </div>

    <v-tooltip color="secondary" left>
      <template v-slot:activator="{ on }">
        <v-btn
          class="mx-2 aic-fab--refresh"
          :class="isSupervisor ? 'supervisor' : ''"
          color="accent"
          fab
          dark
          @click="refresh"
          v-on="on"
        >
          <v-icon>mdi-reload</v-icon>
        </v-btn>
      </template>
      <span v-once> {{ $t('wallboard.refresh') }} </span>
    </v-tooltip>

    <v-tooltip v-if="isWallboard" color="secondary" left>
      <template v-slot:activator="{ on }">
        <v-btn class="mx-2 aic-fab--help" color="accent" fab dark @click="toggleHelp" v-on="on">
          <v-icon>mdi-help</v-icon>
        </v-btn>
      </template>
      <span v-once> {{ $t('wallboard.help') }} </span>
    </v-tooltip>

    <v-tooltip color="secondary" left>
      <template v-slot:activator="{ on }">
        <v-btn
          class="mx-2 aic-fab--secondary"
          color="accent"
          fab
          dark
          @click="toggleWallboardUpdate(!freezeWallboard)"
          v-on="on"
        >
          <v-icon v-if="freezeWallboard">mdi-play</v-icon>
          <v-icon v-else>mdi-pause</v-icon>
        </v-btn>
      </template>
      <span v-if="!freezeWallboard" v-once>
        {{ $t('wallboard.freezeWallboard') }}
      </span>
      <span v-if="freezeWallboard" v-once> {{ $t('wallboard.resumeWallboard') }} </span>
    </v-tooltip>

    <v-tooltip color="secondary" left>
      <template v-slot:activator="{ on }">
        <v-btn class="mx-2 aic-fab" color="accent" fab dark @click="openWallboardConfig" v-on="on">
          <v-icon>mdi-view-dashboard</v-icon>
        </v-btn>
      </template>
      <span v-once>
        {{ $t('wallboard.setup') }}
      </span>
    </v-tooltip>
    <EcsWallboardConfigDialog ref="wallboardConfigDialog" :dashboard-id="dashboardId" />
    <EcsWallboardSlideConfigDialog ref="wallboardSlideConfigDialog" :dashboard-id="dashboardId" />
    <v-navigation-drawer
      v-if="isWallboard"
      v-show="showHelp"
      class="menu-container wallboard-menu-container surface"
      width="375"
      permanent
      absolute
    >
      <v-col>
        <div class="side-bar-content">
          <v-card id="appsideBar" :tile="true" color="surface lighten-1">
            <keep-alive>
              <EcsHelpSideBar class="side-bar-component" />
            </keep-alive>
          </v-card>
        </div>
      </v-col>
    </v-navigation-drawer>
  </div>
</template>

<script>
import Muuri from 'muuri';
import { mapActions, mapGetters } from 'vuex';
import { debounce } from 'lodash';
import EcsWallboardConfigDialog from '@/components/ecs-wallboard-information/EcsWallboardConfigDialog';
import EcsWallboardSlideConfigDialog from '@/components/ecs-wallboard-information/EcsWallboardSlideConfigDialog';
import EcsWallboardCard from '@/components/ecs-wallboard-card/EcsWallboardCard';
import EcsHelpSideBar from '@/components/ecs-app-side-bar/ecs-help-side-bar/EcsHelpSideBar';
import ThemeUtility from '@/components/ecs-theme/ecs-theme-utility';

const SECONDS_TO_UPDATE_THE_STATS = 5;

export default {
  name: 'EcsAgentWallboardInformation',

  components: {
    EcsWallboardCard,
    EcsWallboardConfigDialog,
    EcsWallboardSlideConfigDialog,
    EcsHelpSideBar
  },

  data() {
    return {
      freezeWallboard: false,
      currentCardsOrder: [],
      isLoading: false,
      dashboardId: '',
      supervisorCards: ['EcsSupervisedAgentsSlide', 'EcsSupervisedInteractionsSlide'],
      showHelp: false
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent', 'isWallboard', 'isSupervisor']),
    ...mapGetters('SUPERVISOR', ['selectedSupervisorWallboard']),
    ...mapGetters('WALLBOARD', ['getWallboardCards', 'getWallboardTheme']),
    ...mapGetters('SETTINGS', ['featuresSettings']),

    wallboardCards() {
      const defaultCustomSizeratio = {
        wallboard: {
          width: {
            value: '400',
            type: 'px'
          },
          height: {
            value: '350',
            type: 'px'
          }
        },
        supervisor: {
          default: {
            width: {
              value: '80',
              type: '%'
            },
            height: {
              value: '60',
              type: '%'
            }
          }
        }
      };

      const wallboardCards = this.getWallboardCards.filter(card => card.show === true);
      wallboardCards.map(card => {
        if (this.isSupervisor && !card.customSizeRatio) {
          if (this.isSupervisorCard(card)) {
            card.customSizeRatio = defaultCustomSizeratio.supervisor.default;
          } else {
            card.customSizeRatio = defaultCustomSizeratio.wallboard;
          }
        }
      });

      return wallboardCards;
    },
    wallboardTheme() {
      return this.getWallboardTheme;
    }
  },

  watch: {
    wallboardCards() {
      this.initializeMuuri();
    },
    wallboardTheme() {
      ThemeUtility.build(this.wallboardTheme);
    },
    selectedSupervisorWallboard() {
      this.dashboardId = this.selectedSupervisorWallboard._id;
      this.requestWallboard(this.dashboardId);
      this.initializeMuuri();
    }
  },

  created() {
    // Change the page title to display
    if (!this.isSupervisor) {
      document.title = `eWallboard - ${this.loggedInAgent.username}`;
    }
    this.dashboardId = this.selectedSupervisorWallboard._id;
  },

  mounted() {
    this.$root.$on('edit-selected-wallboard', this.openWallboardConfig);
    if (!this.isSupervisor || (this.isSupervisor && this.dashboardId)) {
      this.requestWallboard(this.dashboardId);
      this.restoreSystemData();

      this.initializeMuuri();
    }
  },

  beforeDestroy() {
    if (this.$root.agentInformationCenterGrid) {
      this.$root.agentInformationCenterGrid.off('dragEnd', this.storeCardsOrder);
      this.$root.agentInformationCenterGrid.destroy(true);
    }
  },

  methods: {
    ...mapActions('WALLBOARD', ['requestWallboard', 'requestWallboardStats', 'updateWallboardCards']),

    ...mapActions('SUPERVISOR', ['requestSupervisedAgents', 'requestSupervisedInteractions']),

    ...mapActions('SYSTEM', ['restoreSystemData']),

    updateStats() {
      if (!this.freezeWallboard) {
        this.requestWallboardStats();
        this.requestSupervisedAgents();
        if (this.isSupervisor) {
          this.requestSupervisedInteractions();
        }
      }
    },

    openWallboardConfig() {
      this.$refs.wallboardConfigDialog.open();
    },

    openCardSettingsModal(card) {
      this.$refs.wallboardSlideConfigDialog.open(card);
    },

    toggleWallboardUpdate(state) {
      this.freezeWallboard = state;
    },

    refresh() {
      window.location.reload();
    },

    toggleHelp() {
      this.showHelp = !this.showHelp;
    },

    onIntersect(entries) {
      if (entries[0] && entries[0].isIntersecting) {
        if (this.$root.updateWallboardCycleRef) {
          clearInterval(this.$root.updateWallboardCycleRef);
        }

        this.updateStats();
        this.$root.updateWallboardCycleRef = setInterval(this.updateStats, SECONDS_TO_UPDATE_THE_STATS * 1000);
      }
    },

    refreshLayout: debounce(async function() {
      await this.$nextTick();
      if (this.$root.agentInformationCenterGrid) {
        this.$root.agentInformationCenterGrid.refreshItems().layout();
      }
    }, 100),

    initializeMuuri: debounce(async function() {
      if (!this.wallboardCards || !this.wallboardCards.length) {
        return;
      }

      await this.$nextTick();
      try {
        if (this.$root.agentInformationCenterGrid) {
          this.$root.agentInformationCenterGrid.destroy();
          delete this.$root.agentInformationCenterGrid;
        }

        this.$root.agentInformationCenterGrid = new Muuri('.grid', {
          dragEnabled: true,
          dragStartPredicate: (item, event) => {
            if (this.isLoading) {
              return false;
            }
            if (event.target.innerHTML === '&nbsp;') {
              return false;
            }
            return Muuri.ItemDrag.defaultStartPredicate(item, event);
          }
        });
        this.$root.agentInformationCenterGrid.on('dragEnd', this.storeCardsOrder);
        this.currentCardsOrder = this.getMuuriItemsOrder();
      } catch {}
    }, 200),

    onResize() {
      setTimeout(this.refreshLayout, 250);
    },

    getMuuriItemsOrder() {
      return this.$root.agentInformationCenterGrid.getItems().map(item => item.getElement().getAttribute('id'));
    },

    storeCardsOrder() {
      const orderedCardsIds = this.getMuuriItemsOrder();

      if (this.currentCardsOrder.join() === orderedCardsIds.join()) {
        return;
      }

      const newCardsOrder = [];
      this.currentCardsOrder = orderedCardsIds;

      orderedCardsIds.forEach(id => {
        newCardsOrder.push(this.wallboardCards.find(card => card._id === id));
      });

      this.isLoading = true;

      this.updateWallboardCards({ cards: newCardsOrder, dashboardId: this.dashboardId }).finally(() => {
        this.isLoading = false;
      });
    },

    isSupervisorCard(card) {
      return this.supervisorCards.includes(card.componentName);
    },

    customCardSize(card) {
      return this.isSupervisor
        ? {
            width: card.customSizeRatio ? card.customSizeRatio.width.value + card.customSizeRatio.width.type : '',
            height: card.customSizeRatio ? card.customSizeRatio.height.value + card.customSizeRatio.height.type : ''
          }
        : {};
    }
  }
};
</script>

<style src="./scoped.css" scoped />

<style>
#agentInformationCenter.is-supervisor .muuri {
  height: 100% !important;
}

#agentInformationCenter.is-supervisor .total-stat-container {
  padding: 0;
  margin: 0;
  width: 100%;
}

#agentInformationCenter.is-supervisor .total-stat-container .table-container {
  margin: 5px 0;
}

#agentInformationCenter.is-supervisor .item {
  max-width: 100%;
}

#agentInformationCenter.is-supervisor .item .item-content {
  width: 100%;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
}
</style>
